import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerBremser() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" },
    { name: "Trailer med og uden bremser", path: "/trailer-bremser" }
  ];
  const tocItems = [
    { id: "regler-med-uden-bremser", title: "Regler for trailer med og uden bremser" },
    { id: "paaloebsbremse", title: "Sådan virker en påløbsbremse" },
    { id: "springwire", title: "Sikkerhedswire (springwire): Lovkrav og montering" },
    { id: "bremser-haenger", title: "Når trailerens bremser hænger eller sidder fast" },
    { id: "bremser-naar-du-bakker", title: "Hvorfor bremser traileren, når jeg bakker?" },
    { id: "vedligeholdelse", title: "Vedligeholdelse og justering af bremser" }
  ];

  const faq = [
    {
      q: "Skal alle trailere have bremser?",
      a: "Nej. Trailere med en tilladt totalvægt på højst 750 kg behøver normalt ikke have bremser, forudsat at trækbilen ifølge sin registreringsattest er godkendt til at trække trailerens vægt uden bremser. Trailere med en tilladt totalvægt over 750 kg skal altid være forsynet med driftsbremser."
    },
    {
      q: "Hvad gør jeg, hvis trailerbremserne sidder fast efter at have stået stille?",
      a: "Bremsebakker ruster ofte fast til bremsetromlerne, især hvis håndbremsen har været trukket under fugtigt vejr. Prøv forsigtigt at bakke og køre frem et par gange, eller bank let på bremsetromlen med en gummihammer. Undlad altid at efterlade traileren langtidsparkeret med håndbremsen trukket."
    },
    {
      q: "Må sikkerhedswiren blot lægges løst hen over trækkrogen?",
      a: "Nej, på biler med et dertil indrettet øje eller fastgørelsespunkt på trækket skal wiren fastgøres i dette øje. Hvis trækket ikke har et øje, skal der monteres et godkendt sikkerhedsbeslag eller en fast spændbøjle på trækket."
    },
    {
      q: "Hvorfor blokerer traileren, når jeg bakker op ad en bakke?",
      a: "Ældre eller dårligt justerede påløbsbremser mangler en velfungerende bakautomatik (Rückfahrautomatik). Når du bakker, skubbes påløbsdæmperen ind, og bremserne aktiveres. Hvis bakautomatikken svigter, skal bremsebakkerne justeres eller renses."
    }
  ];

  const sources = [
    { title: "Bekendtgørelse om køretøjers indretning og udstyr (BEK nr. 154 af 28/01/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/154" },
    { title: "Bekendtgørelse om godkendelse og syn af køretøjer (BEK nr. 1429 af 29/11/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1429" }
  ];

  return (
    <>
      <SEO 
        title="Trailerbremser: Regler, påløbsbremse og fejlfinding" 
        description="Regler for trailer med og uden bremser. Forstå påløbsbremse, korrekt montering af sikkerhedswire og hvad du gør når bremserne hænger." 
        pathname="/trailer-bremser"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailer med og uden bremser: Regler og fejlfinding
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          I Danmark skal påhængskøretøjer med en tilladt totalvægt på over 750 kg altid være forsynet med et godkendt driftsbremseanlæg. For personbiler er dette i praksis altid en mekanisk påløbsbremse med bakautomatik.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Ubremsede trailere må have en tilladt totalvægt på højst 750 kg, men din bils registreringsattest fastsætter ofte en lavere grænse for ubremset vægt (typisk 500–750 kg). Hænger bremserne, eller bremser traileren skævt, skyldes det typisk rustne bremsekabler, slidte bremsebakker eller en defekt påløbsdæmper.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Vigtig tommelfingerregel">
          <p className="m-0">
            Forlad <strong>aldrig</strong> en trailer parkeret med håndbremsen trukket over længere tid. Fugt og snavs får bremsebakkerne til at ruste fast til tromlerne på få uger. Brug i stedet solide stopklodser under hjulene.
          </p>
        </Callout>

        <h2 id="regler-med-uden-bremser" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Regler for trailer med og uden bremser
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Færdselslovgivningen og detailforskrifterne for køretøjer opdeler påhængskøretøjer i to kategorier efter bremsesystem:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Trailertype</th>
                <th className="py-2.5 pr-4 font-semibold">Tilladt totalvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Bremsekrav</th>
                <th className="py-2.5 font-semibold">Krav til trækbil</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Ubremset trailer (O1)</td>
                <td className="py-3 pr-4">Højst 750 kg</td>
                <td className="py-3 text-[#5C636B]">Ingen bremser påkrævet</td>
                <td className="py-3">Faktisk vægt skal være under felt O.2</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Bremset trailer (O1/O2)</td>
                <td className="py-3 pr-4">Op til 3.500 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">Påløbsbremse + håndbremse</td>
                <td className="py-3">Faktisk vægt skal være under felt O.1</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om bilens trækevne på registreringsattesten i artiklen om <Link to="/hvor-meget-maa-man-traekke" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">hvor meget du må trække</Link>.
        </p>

        <h2 id="paaloebsbremse" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sådan virker en påløbsbremse
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          En påløbsbremse er en rent mekanisk konstruktion, der ikke kræver nogen elektrisk eller hydraulisk forbindelse til bilens bremsesystem:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Når trækbilen bremser, vil trailerens inerti presse traileren fremad mod bilen.</li>
          <li>Trailerens koblingshoved glider ind i trækstangen mod en indbygget hydraulisk dæmper (påløbsdæmperen).</li>
          <li>Denne indadgående bevægelse trækker i en bremsevægtstang under traileren, som via bremsekabler aktiverer bremsebakkerne inde i hjultromlerne.</li>
          <li>Når bilen slipper bremsen og accelererer, trækkes koblingshovedet ud igen, og fjedre i bremserne slipper tromlerne.</li>
        </ol>

        <h2 id="springwire" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sikkerhedswire (springwire): Lovkrav og montering
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Alle bremsede trailere skal ifølge færdselslovgivningen være forsynet med en sikkerhedswire (også kaldet en katastrofewire eller springwire).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Formålet med wiren er, at hvis traileren under kørslen river sig løs fra bilens træk, vil wiren trække håndbremsen hårdt til og bringe traileren til standsning, hvorefter en specialklemme udløses eller wiren knækker.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          <strong>Krav til fastgørelse:</strong> Wiren skal fastgøres i et dertil indrettet øje eller fastgørelsespunkt på trækket. Den må i Danmark kun lægges som en løkke over selve trækkuglen, hvis trækkrogen har en udformning eller krave, der hindrer wiren i at glide af ved et uheld, og der ikke findes et fastgørelsesøje.
        </p>

        <h2 id="bremser-haenger" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Når trailerens bremser hænger eller sidder fast
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          At en trailer bremser skævt eller hænger på det ene hjul, er den hyppigste fejl ved syn og kontrol. Symptomerne er typisk varme fælge, røglugt fra tromlerne eller at traileren trækker til siden under bremsning.
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Tærede bremsekabler:</strong> Vand trænger ind i kabelkapperne, ruster stålwiren og forhindrer den i at returnere. Løsning: Udskiftning af kabler.</li>
          <li><strong>Fastrustede expandere:</strong> Det mekaniske led inde i tromlen, der presser bakkerne ud, gror fast i bremsestøv og fugt. Løsning: Adskillelse, rensning og smøring med varmebestandigt bremsefedt.</li>
          <li><strong>Slidt påløbsdæmper:</strong> Hvis bilen hugger voldsomt ved let bremsning, er dæmperen i trækstangen defekt og skal udskiftes.</li>
        </ul>

        <h2 id="bremser-naar-du-bakker" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvorfor bremser traileren, når jeg bakker?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Moderne trailere er udstyret med <em>bakautomatik</em>. Når hjulene roterer baglæns, vipper bremsebakkerne en smule indad, så tromlen kan dreje frit rundt, selvom påløbsdæmperen trykkes ind.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Hvis traileren blokerer og nægter at bakke, skyldes det næsten altid, at bremserne er justeret for stramt ude på bremsekablerne i stedet for direkte på stjerneskruen inde i tromlen. Derved kan bakautomatikken ikke udløse bakkerne.
        </p>

        <h2 id="vedligeholdelse" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Vedligeholdelse og justering af bremser
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          En påløbsbremse bør kontrolleres mindst én gang årligt:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Smør smøreniplerne på trækstangen med fedtsprøjte, indtil nyt fedt trænger frem.</li>
          <li>Hæv traileren op på bukke og drej hjulene for at lytte efter skrabelyde fra slidte hjullejer eller løse fjedre.</li>
          <li>Kontrollér at påløbsstangen kan trykkes ind med håndkraft og selv glider roligt ud igen.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Se også vores gennemgang af <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">synsregler for trailer</Link> samt de elektriske krav under <Link to="/trailer-lys-regler" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">regler for trailerlygter</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
